import InitialsAvatar from './InitialsAvatar';
import { ErrorState, SkeletonCard } from './ui';

function BoardCard({ member }) {
  return (
    <article className="overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-ink/[0.06]">
      {/* Image-forward portrait. Real photos fill the frame; members without a
          photo show a clean maroon -> gold initials tile. */}
      <div className="relative aspect-square overflow-hidden bg-maroon-50">
        <InitialsAvatar name={member.full_name} photoPath={member.photo_url || member.photo_path} fill />
      </div>

      <div className="p-5">
        <h3 className="font-display text-lg font-bold leading-snug text-ink">{member.full_name}</h3>
        {member.position && (
          <p className="mt-1 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-maroon-700">
            {member.position}
          </p>
        )}
        {member.club_name && (
          <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">{member.club_name}</p>
        )}
      </div>
    </article>
  );
}

/**
 * Responsive board / leadership grid (1 / 2 / 3 columns).
 *
 * Members render in the exact order supplied (the API's admin-managed
 * `board_order` sequence); this component does not re-sort.
 *
 * @param {Array}   members  - leadership members from `membersApi.list({ leadership: 1 })`.
 * @param {boolean} loading  - show skeletons while fetching.
 * @param {object}  error    - normalized API error.
 * @param {fn}      onRetry  - reload handler for the error state.
 */
export default function BoardGrid({ members = [], loading = false, error = null, onRetry }) {
  if (error) return <ErrorState error={error} onRetry={onRetry} />;

  if (loading) {
    return (
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (!members.length) {
    return <p className="text-center text-ink-muted">Board details are coming soon.</p>;
  }

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {members.map((m) => (
        <BoardCard key={m.id} member={m} />
      ))}
    </div>
  );
}
